import {
  KeyboardDatePicker,
  KeyboardDatePickerProps,
} from '@material-ui/pickers';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { isValid, startOfDay } from 'date-fns';
import { NullableDate, NullableString } from 'mapistry-shared';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { ConditionalWrapper } from '../ConditionalWrapper';
import {
  getCommonPickerProps,
  muiPickerCss,
  MuiPickerPropsToOmitOnWrapper,
  PickerInputLabel,
  PickerSubText,
  useOnChangeWrapper,
} from './shared';

type DatePickerProps = Omit<
  KeyboardDatePickerProps,
  MuiPickerPropsToOmitOnWrapper | 'format' | 'hiddenLabel'
> & {
  errorText?: string;
  isCompact?: boolean;
  isErrorTextHidden?: boolean;
  isLabelHidden?: boolean;
  name: string; // add name for unique ID
  subText?: NullableString;
  isSubtextHidden?: boolean;
  // this better matches the signature for html input & final form field's onChange
  onChange: (event: { target: { value: NullableDate } }) => void;
  tooltipText?: string;
  dontSaveSpaceForErrorText?: boolean;
};

const StyledDatePicker = styled(KeyboardDatePicker)<{
  $isCompact?: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.white};
  ${muiPickerCss}
  ${({ $isCompact }) =>
    $isCompact &&
    css`
      & .MuiFormControlLabel-root {
        margin-right: 0;
      }

      & .MuiInputBase-root {
        min-width: 7.5rem;
        padding: 0 0 0 0.5rem;
      }

      & .MuiInputAdornment-positionEnd {
        margin-right: -8px;
        margin-left: -8px;
      }
    `};
`;

export const DATE_PICKER_DISPLAY_FORMAT = 'MM/dd/yyyy';

export function DatePicker({
  className,
  error,
  errorText,
  isCompact,
  isErrorTextHidden,
  isLabelHidden = false,
  subText,
  label,
  tooltipText,
  dontSaveSpaceForErrorText = false,
  name,
  onChange: onChangeProp,
  required,
  value,
  disabled,
  isSubtextHidden,
  ...rest
}: DatePickerProps) {
  const [idRandomify] = useState(uuidv4);
  const pickerInputElId = `${name}-id-${idRandomify}`;
  // Only apply `startOfDay` when MUI hands us a genuinely valid date. When the
  // user is mid-edit with partial/invalid text, MUI gives us an Invalid Date —
  // pass it through unchanged so MUI's internal reset effect (which only fires
  // when value is null or valid) leaves the typed text alone.
  const onChange = useOnChangeWrapper(onChangeProp, (date) =>
    date && isValid(date) ? startOfDay(date) : date,
  );
  const labelComponent = (
    <PickerInputLabel
      required={required}
      error={error}
      htmlFor={pickerInputElId}
      label={label}
      tooltipText={tooltipText}
    />
  );

  return (
    <div className={className}>
      {isLabelHidden ? (
        <VisuallyHidden>{labelComponent}</VisuallyHidden>
      ) : (
        labelComponent
      )}
      <StyledDatePicker
        required={required}
        error={error}
        onChange={onChange}
        value={value || null} // an unselected date should have value === null
        format={DATE_PICKER_DISPLAY_FORMAT}
        initialFocusedDate={new Date()}
        disabled={disabled}
        $isCompact={isCompact}
        {...getCommonPickerProps(pickerInputElId)}
        {...rest}
      />
      {(!dontSaveSpaceForErrorText || !!errorText || !!subText) && (
        <ConditionalWrapper
          condition={!!isErrorTextHidden}
          /* eslint-disable-next-line react/no-unstable-nested-components */
          wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
        >
          <PickerSubText
            error={error}
            errorText={errorText}
            subText={subText}
            isSubtextHidden={isSubtextHidden}
          />
        </ConditionalWrapper>
      )}
    </div>
  );
}
